import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';

interface QuizTimerProps {
  /** Total time in seconds */
  totalSeconds: number;
  /** Callback when time runs out */
  onTimeUp: () => void;
  /** Whether the timer is paused */
  paused?: boolean;
}

/**
 * Quiz Timer Component
 * Displays a countdown timer and triggers onTimeUp when time expires
 */
export const QuizTimer = ({ totalSeconds, onTimeUp, paused = false }: QuizTimerProps) => {
  const [timeRemaining, setTimeRemaining] = useState(totalSeconds);

  useEffect(() => {
    if (paused) {
      return;
    }

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [paused, onTimeUp]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const isLowTime = timeRemaining < 60;
  const isCriticalTime = timeRemaining < 30;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 2,
        backgroundColor: isCriticalTime ? 'error.light' : isLowTime ? 'warning.light' : 'grey.100',
        color: isCriticalTime ? 'error.contrastText' : isLowTime ? 'warning.contrastText' : 'text.primary',
        transition: 'all 0.3s',
      }}
    >
      <AccessTimeIcon
        sx={{
          animation: isLowTime ? 'pulse 1s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.5 },
            '100%': { opacity: 1 },
          },
        }}
      />
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 'bold',
        }}
      >
        {formatTime(timeRemaining)}
      </Typography>
    </Box>
  );
};
